import { useState } from "react";

import { AttributionFields } from "@/components/AttributionFields";

export type HeroLeadFormVariant = "industrie" | "tiefgarage";

interface HeroLeadFormProps {
  pageId: string;
  /** E.164 phone for the fallback link, e.g. "+4928411931010" */
  phoneTel: string;
  /** Pretty phone for display, e.g. "+49 2841 931010" */
  phoneDisplay: string;
  /** Options for the "Worum geht es?" select. */
  topicOptions: string[];
  /** Determines headline, intro text, placeholder and trust line copy. */
  variant: HeroLeadFormVariant;
  /** Stable, human readable form name, e.g. "Industrieboden V1 Hero Formular". */
  formName: string;
}


/**
 * High-attention contact-card rendered inside the hero on landing pages.
 *
 * Copy is driven by `variant` so the same component can serve both the
 * Industrieboden and Tiefgarage landing pages without divergent components.
 *
 * Submission uses the same `/api/public/lead-cf7` pipe as the long form
 * lower on the page (via `submitLead`) and fires the same `generate_lead`
 * GTM event with the page_id.
 */
export function HeroLeadForm({
  pageId,
  phoneTel,
  phoneDisplay,
  topicOptions,
  variant,
  formName,
}: HeroLeadFormProps) {
  const formId = `${pageId}_hero`;
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");


  const copy =
    variant === "industrie"
      ? {
          headline:
            "Erste Einschätzung für Ihre Industriebodenbeschichtung anfragen",
          intro:
            "Senden Sie uns kurz die wichtigsten Eckdaten zu Objekt, Fläche und Zustand des Bodens. Wir prüfen Ihre Anfrage und melden uns in der Regel innerhalb von 24 Stunden mit einer ersten Einschätzung.",
          trust:
            "Unverbindlich · Für gewerbliche / industrielle Objekte · Antwort in 24 Stunden",
          placeholder:
            "z. B. Produktionshalle, Lagerfläche oder Werkstattboden, ca. 500 m², Boden abgenutzt, staubt oder soll neu beschichtet werden.",
        }
      : {
          headline:
            "Erste Einschätzung für Ihre Tiefgaragenbeschichtung anfragen",
          intro:
            "Senden Sie uns kurz die wichtigsten Eckdaten zu Objekt, Fläche und Zustand des Bodens. Wir prüfen Ihre Anfrage und melden uns in der Regel innerhalb von 24 Stunden mit einer ersten Einschätzung.",
          trust:
            "Unverbindlich · Für gewerbliche / institutionelle Objekte · Antwort in 24 Stunden",
          placeholder:
            "z. B. Tiefgarage in Köln, ca. 80 Stellplätze, Boden staubt, ist abgenutzt oder soll neu beschichtet werden.",
        };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();
    setStatus("sending");
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);
    const payload = Object.fromEntries(fd.entries()) as Record<string, string>;
    try {
      const { submitLead } = await import("@/lib/submit-lead");
      await submitLead(
        {
          name: payload.name || "",
          firma: payload.firma || "",
          email: payload.email || "",
          telefon: payload.telefon || "",
          ort: "",
          betreff: payload.betreff || "",
          nachricht: payload.nachricht || "",
        },
        pageId,
        payload.website || "",
        formEl
      );

      if (typeof window !== "undefined") {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: "generate_lead",
          page_id: pageId,
          form_location: "hero",
        });
      }
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  const inputClass =
    "w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";
  const labelClass =
    "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-foreground/70";

  if (status === "success") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="relative rounded-2xl border-2 border-primary bg-background p-6 shadow-2xl shadow-primary/30 md:p-7"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <svg
            viewBox="0 0 24 24"
            className="h-6 w-6 fill-none stroke-current"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12l5 5L20 7" />
          </svg>
        </div>
        <h3 className="mt-4 text-2xl font-semibold tracking-tight text-foreground">
          Vielen Dank für Ihre Anfrage
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Wir haben Ihre Anfrage erhalten. Wir prüfen Ihre Angaben und melden
          uns in der Regel innerhalb von 24 Stunden mit einer ersten
          Einschätzung.
        </p>
        <p className="mt-4 text-sm text-foreground">
          Lieber anrufen?{" "}
          <a
            href={`tel:${phoneTel}`}
            className="font-semibold text-primary hover:underline"
          >
            {phoneDisplay}
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Subtle glow halo behind the card to make it pop on the dark hero. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-2 rounded-2xl bg-primary/20 blur-2xl"
      />
      <div className="relative rounded-2xl border-2 border-primary bg-background p-5 shadow-2xl shadow-primary/30 md:p-6">
        {/* Top accent badge */}
        <div className="-mt-9 mb-4 flex justify-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary-foreground shadow-md">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
            Kostenlose Erstberatung
          </span>
        </div>

        <h2 className="text-lg font-semibold leading-tight tracking-tight text-foreground md:text-xl">
          {copy.headline}
        </h2>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          {copy.intro}
        </p>
        <p className="mt-2 text-xs font-medium text-primary">{copy.trust}</p>

        <form
          onSubmit={onSubmit}
          id={formId}
          data-gj-no-native-submit="1"
          data-form-id={formId}
          data-form-name={formName}
          className="mt-4 space-y-3"
        >
          <AttributionFields />

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor={`${pageId}-hero-name`} className={labelClass}>
                Name *
              </label>
              <input
                id={`${pageId}-hero-name`}
                name="name"
                required
                autoComplete="name"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={`${pageId}-hero-firma`} className={labelClass}>
                Firma *
              </label>
              <input
                id={`${pageId}-hero-firma`}
                name="firma"
                required
                autoComplete="organization"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={`${pageId}-hero-email`} className={labelClass}>
                E-Mail *
              </label>
              <input
                id={`${pageId}-hero-email`}
                name="email"
                type="email"
                required
                autoComplete="email"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={`${pageId}-hero-tel`} className={labelClass}>
                Telefon *
              </label>
              <input
                id={`${pageId}-hero-tel`}
                name="telefon"
                type="tel"
                required
                autoComplete="tel"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor={`${pageId}-hero-betreff`} className={labelClass}>
              Worum geht es? *
            </label>
            <select
              id={`${pageId}-hero-betreff`}
              name="betreff"
              required
              defaultValue=""
              className={inputClass}
            >
              <option value="" disabled>
                Bitte wählen
              </option>
              {topicOptions.map((opt) => (
                <option key={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor={`${pageId}-hero-nachricht`} className={labelClass}>
              Kurze Beschreibung *
            </label>
            <textarea
              id={`${pageId}-hero-nachricht`}
              name="nachricht"
              required
              rows={3}
              placeholder={copy.placeholder}
              className={inputClass}
            />
          </div>

          {/* Honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            className="hidden"
            aria-hidden="true"
          />

          <button
            type="submit"
            disabled={status === "sending"}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:bg-primary/90 hover:shadow-lg disabled:opacity-60"
          >
            {status === "sending" ? "Wird gesendet …" : "Erste Einschätzung anfragen"}
            {status !== "sending" && (
              <svg
                viewBox="0 0 20 20"
                className="h-4 w-4 fill-current"
                aria-hidden="true"
              >
                <path d="M7.05 4.05a1 1 0 011.414 0l5.243 5.243a1 1 0 010 1.414L8.464 15.95a1 1 0 11-1.414-1.414L11.586 10 7.05 5.464a1 1 0 010-1.414z" />
              </svg>
            )}
          </button>

          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Nach dem Absenden prüfen wir Ihre Angaben. Wenn das Projekt passt,
            melden wir uns zur ersten Einschätzung oder zur Abstimmung der
            nächsten Schritte.
          </p>

          <p className="text-center text-[11px] text-muted-foreground">
            Lieber anrufen?{" "}
            <a
              href={`tel:${phoneTel}`}
              className="font-semibold text-primary hover:underline"
            >
              {phoneDisplay}
            </a>
          </p>

          {status === "error" && (
            <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
              Beim Senden ist etwas schiefgegangen. Bitte versuchen Sie es
              erneut oder rufen Sie uns direkt an.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
